import * as Dialog from '@radix-ui/react-dialog'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Loader2, TriangleAlert } from 'lucide-react'
import { Fragment, useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import { useTRPC } from '@/lib/trpc'

type ForfeitTarget = {
  actorId: string
  editionId: string
  gameId: string
  participationId: string
}

export function TournamentGameForfeit({
  editionId,
  gameId,
  onSettled,
}: {
  editionId: string
  gameId: string
  onSettled: () => Promise<unknown>
}) {
  const { actor } = useAuth()
  const { t } = useI18n()
  const trpc = useTRPC()
  const options = trpc.tournamentRouter.getProgress.queryOptions({ editionId })
  const progressContext = {
    ...options.queryKey[1],
    actorId: actor?.id ?? null,
  }
  const progressQuery = useQuery({
    ...options,
    queryKey: [
      options.queryKey[0],
      progressContext,
    ],
    enabled: !!actor,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 15_000,
  })
  const mutation = useMutation(trpc.tournamentRouter.forfeit.mutationOptions())
  const [target, setTarget] = useState<ForfeitTarget | null>(null)
  const [failed, setFailed] = useState(false)
  const [pending, setPending] = useState(false)
  const contextRef = useRef({ actorId: actor?.id, editionId, gameId })
  contextRef.current = { actorId: actor?.id, editionId, gameId }
  const current
    = progressQuery.data?.editionId === editionId
      ? progressQuery.data.current
      : undefined
  const visibleTarget
    = target && target.actorId === actor?.id
      && target.editionId === editionId
      && target.gameId === gameId
      ? target
      : null
  useEffect(() => {
    setTarget(null)
    setFailed(false)
    setPending(false)
  }, [actor?.id, editionId, gameId])
  const confirm = async () => {
    if (!visibleTarget || pending || mutation.isPending)
      return
    const captured = visibleTarget
    const isCurrent = () =>
      contextRef.current.actorId === captured.actorId
      && contextRef.current.editionId === captured.editionId
      && contextRef.current.gameId === captured.gameId
    setFailed(false)
    setPending(true)
    try {
      await mutation.mutateAsync({
        editionId: captured.editionId,
        participationId: captured.participationId,
      })
      if (!isCurrent())
        return
      const progressResult = await progressQuery.refetch()
      if (!isCurrent())
        return
      if (progressResult.isError) {
        setFailed(true)
        return
      }
      const statusResult = await onSettled()
      if (!isCurrent())
        return
      if (
        typeof statusResult !== 'object'
        || statusResult === null
        || !('isError' in statusResult)
        || statusResult.isError !== false
      ) {
        setFailed(true)
        return
      }
      setTarget(null)
    }
    catch {
      if (isCurrent()) {
        setFailed(true)
        await progressQuery.refetch().catch(() => {})
      }
    }
    finally {
      if (isCurrent())
        setPending(false)
    }
  }
  if (!actor || (current?.gameId !== gameId && !visibleTarget))
    return null
  return (
    <Dialog.Root
      open={!!visibleTarget}
      onOpenChange={(open) => {
        if (pending || mutation.isPending)
          return
        if (!open) {
          setFailed(false)
          setTarget(null)
          return
        }
        if (current?.gameId !== gameId)
          return
        setFailed(false)
        setTarget({
          actorId: actor.id,
          editionId,
          gameId,
          participationId: current.id,
        })
      }}
    >
      <Dialog.Trigger asChild>
        <Button
          type="button"
          variant="outline"
          className="min-h-11"
          data-testid="tournament-game-forfeit"
        >
          {t('tournamentForfeit.action')}
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/60" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-[60] max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl border border-border bg-card p-6 text-foreground shadow-xl outline-none"
          onInteractOutside={event => event.preventDefault()}
          onEscapeKeyDown={(event) => {
            if (pending || mutation.isPending)
              event.preventDefault()
          }}
        >
          <TriangleAlert
            className="mx-auto mb-4 size-10 text-primary"
            aria-hidden="true"
          />
          <Dialog.Title className="text-center text-2xl font-semibold">
            {t('tournamentForfeit.title')}
          </Dialog.Title>
          <Dialog.Description asChild>
            <div className="mt-4 space-y-3 text-center text-sm leading-relaxed">
              <p>{t('tournamentForfeit.rule')}</p>
              <p className="text-muted-foreground">
                {t('tournamentForfeit.warning')
                  .split('<br>')
                  .map((line, index) => (
                    <Fragment key={line}>
                      {index > 0 && <br />}
                      {line}
                    </Fragment>
                  ))}
              </p>
            </div>
          </Dialog.Description>
          {failed && (
            <p
              role="alert"
              className="mt-4 text-sm"
              data-testid="tournament-game-forfeit-error"
            >
              {t('forum.errorGeneric')}
            </p>
          )}
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <Dialog.Close asChild>
              <Button
                type="button"
                variant="outline"
                disabled={pending || mutation.isPending}
                className="min-h-12 whitespace-normal"
              >
                {t('tournamentForfeit.continue')}
              </Button>
            </Dialog.Close>
            <Button
              type="button"
              disabled={pending || mutation.isPending}
              onClick={() => {
                void confirm()
              }}
              className="min-h-12 whitespace-normal"
              data-testid="tournament-game-forfeit-confirm"
            >
              {(pending || mutation.isPending) && (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              )}
              {t('tournamentForfeit.confirm')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
